import type { CategoryPrompt } from "../types";

import React from "react";

import {
  Box,
  Droplet,
  Droplets,
  HeartHandshake,
  RotateCcw,
  SprayCan,
} from "lucide-react";

export const personalCarePrompts: CategoryPrompt[] = [
  {
    icon: <SprayCan />,
    title: "Ingredient Education Landing Page",
    description:
      "Create an educational page explaining harmful ingredients and why natural alternatives work, with transparent ingredient lists and CTAs.",
    prompt: `**Build an ingredient education landing page that exposes harmful chemicals and positions your natural formulations as the safer, effective alternatives.**

**EDUCATION FOCUS:** e.g., "What's Really in Your [Product]?" OR "Clean Beauty Explained" OR "The Ingredients We'll Never Use"

**FORMULATION ANGLE:** e.g., Natural alternatives, clean formulas, toxin-free promise

**Reader Type:** Choose one:
- [ ] Ingredient-conscious (research everything)
- [ ] Health-concerned (worried about chemicals)
- [ ] Clean beauty converts (seeking trustworthy brands)

**Tone & Style:**
- Educational without fear-mongering
- Science-backed and credible
- Empowering through knowledge
- Transparent and honest

**CTA Guidelines:** Each CTA should:
- Reference clean formulation ("Shop Toxin-Free")
- Use safety language ("Safe for Daily Use")
- Include transparency ("See Full Ingredients")
- Emphasize effectiveness ("Clean AND Works")

**Conversion Tactics Use:**
- Harmful vs. safe ingredient comparisons
- Research citations and studies
- Full ingredient transparency per product
- Certification badge displays
- Dermatologist endorsements
- Before/after from switching to clean

**Key Elements to Include:**
- **Hero Section:** Bold transparency statement, "See What's Inside" CTA
- **Our Clean Philosophy:** What clean means to you (2-3 paragraphs)
- **Never List:** 10-15 harmful ingredients you exclude with brief why-harmful
- **Ingredient Deep Dives (6-10):**
  - Harmful ingredient name
  - What it does in conventional products
  - Health/skin concerns (scientific backing)
  - Long-term effects
  - Your natural alternative
  - How natural alternative works
  - Efficacy research
- **Full Ingredient Disclosure:** Complete formula lists for each product with explanations
- **Comparison Chart:** Toxic conventional vs. your clean formulas (5-8 rows)
- **Scientific Backing:** Study references, research links (8-12 citations)
- **Certification Display:** EWG Verified, cruelty-free, organic seals with explanations
- **What We Use Instead:** Safe ingredient alternatives detailed
- **Ingredient Glossary:** Educational explainers for ingredients you do use
- **Dermatologist Endorsement:** Expert validation quotes
- **Efficacy Proof:** Clean doesn't mean less effective - show results
- **Sourcing Transparency:** Where ingredients come from
- **Safe for Sensitive Skin:** Hypoallergenic testing, irritation data
- **Product Ingredient Pages:** Each product with full transparency
- **CTAs:** Hero, after never list, with products, after certifications (5-7 total)
- **FAQ:** 6-8 questions (Really as effective? All natural? Preservatives? Shelf life? Pregnancy-safe? Testing?)

**Goal:** Convert ingredient-conscious shoppers into loyal customers by providing radical transparency, scientific education on harmful chemicals, and proof that your clean formulations deliver equal or superior results without compromising skin health or long-term safety through comprehensive ingredient disclosure.`,
  },
  {
    icon: <Droplets />,
    title: "Toxic Ingredient Listicle Advertorial",
    description:
      "Build a listicle covering harmful ingredients, with each linking to research and positioning my clean formula as the safer alternative.",
    prompt: `**Build a toxic ingredient listicle that exposes harmful chemicals and positions your clean formula as the scientifically-backed safer alternative.**

**TOPIC:** e.g., "7 Toxic Ingredients Hiding in Your [Product Type]" OR "5 Chemicals Harming Your Health" OR "Why Your [Product] May Be Dangerous"

**YOUR CLEAN FORMULA:** e.g., "Our toxin-free, clinically-safe alternative"

**Reader Type:** Choose one:
- [ ] Health-conscious (avoid toxins actively)
- [ ] Ingredient-scrutinizers (read every label)
- [ ] Safety-concerned (family/pet/environmental focus)

**Tone & Style:**
- Educational exposé without fear-mongering
- Science-backed not sensational
- Empowering through transparency
- Solution-focused ending

**CTA Guidelines:** Each CTA should:
- Reference clean formulation ("Shop Toxin-Free")
- Use safety language ("Clinically Safe")
- Include research backing ("Science-Backed")
- Emphasize effectiveness ("Clean AND Works")

**Conversion Tactics Use:**
- Research links to peer-reviewed studies
- Regulatory body citations (EPA, FDA, EWG)
- Your third-party testing results
- Side-by-side ingredient comparisons
- Doctor/toxicologist endorsements
- Full ingredient transparency

**Key Elements to Include:**
- **Hook:** Shocking toxin revelation (2-3 paragraphs on hidden dangers)
- **List Items (6-9 toxic ingredients):** Numbered harmful ingredient → research → your clean alternative
- **Ingredient Structure Per Item:**
  - Toxic ingredient headline: "#X: [Chemical name] (Found in X% of products)"
  - What it is: Chemical purpose in traditional formulas
  - The harm: Health effects with research citations
  - Research links: Peer-reviewed studies (2-4 per ingredient)
  - Regulatory status: FDA/EPA/international restrictions
  - Long-term risks: Cumulative exposure dangers
  - Your alternative: Safe ingredient that performs same function
  - How yours works: Science of safe ingredient efficacy
  - Safety profile: Research on your alternative
  - Customer testimonial: Someone who switched for health reasons
- **Product Callouts:** Clean ingredient highlights with "Shop Safe" CTA
- **Comparison Chart:** Toxic conventional vs. your clean formula (5-7 rows)
- **Full Ingredient Disclosure:** Your complete formula with safety explanations
- **Third-Party Testing:** Independent lab results on safety/purity
- **Research Citations:** Formatted links to studies (15-25 total)
- **Expert Validation:** Toxicologist, doctor, or scientist endorsement
- **Certification Badges:** EWG Verified, Made Safe, dermatologist-tested
- **What's NOT In Yours:** Comprehensive exclusion list with reasons
- **Efficacy Proof:** Clean doesn't mean less effective - show results
- **Safety for Vulnerable Populations:** Kids, pregnant, pets, etc.
- **CTAs:** After ingredient #2, after ingredient #5, conclusion (5-7 total)
- **FAQ:** 5-7 questions (Really as effective? All natural? Shelf life? Cost more? Safe for everyone?)

**Goal:** Convert health-conscious shoppers into loyal customers by exposing harmful chemicals with credible research, then proving your clean formula delivers equal or superior results without health risks - building trust through radical transparency and scientific validation that makes switching feel like an obvious health investment.`,
  },
  {
    icon: <HeartHandshake />,
    title: "Skin Type Diagnostic Quiz",
    description:
      "Build a quiz diagnosing skin type and things like sensitivity triggers, then recommend my products with expert endorsement.",
    prompt: `**Build a skin type diagnostic quiz that identifies specific skin characteristics and sensitivities to recommend targeted product solutions with professional validation.**

**QUIZ FOCUS:** e.g., "What's Your True Skin Type?" OR "Discover Your Skin Profile" OR "Custom Skincare Diagnostic"

**DIAGNOSTIC RANGE:** e.g., Type (oily, dry, combo, normal), concerns (acne, aging, sensitivity), triggers (15-30 products to match from)

**Quiz Taker Type:** Choose one:
- [ ] Type-confused (using wrong products for skin)
- [ ] Sensitivity sufferers (reactions, need gentle)
- [ ] Results-frustrated (current routine not working)

**Tone & Style:**
- Clinical diagnostic approach
- Dermatologist-level expertise
- Personalized and specific
- Empowering through understanding

**Quiz Question Guidelines:** Each question should:
- Focus on symptoms and reactions
- Use dermatological terminology accessibly
- Build toward root cause identification
- Address environmental/lifestyle factors
- 8-10 total questions maximum

**Email Gate Strategy:**
- Place after question #6 (deep commitment)
- Frame as "Get your personalized skin diagnosis + routine"
- Offer incentive: "Unlock diagnosis + 25% off recommended products"
- Single field: email only
- Trust line: "Join 250,000+ with healthier skin"

**Results Page Elements:**
- **Diagnosis Headline:** "Your skin type: [Primary Type + Sensitivity Profile]"
- **Skin Profile Detailed:** Clinical explanation of their skin characteristics (2-3 paragraphs)
- **Key Characteristics:** What defines their skin type
- **Sensitivity Triggers Identified:** Specific ingredients/factors to avoid
- **Recommended Routine:** 3-6 products in application order
- **Product Details:** Why each is perfect for their diagnosed type
- **Ingredient Targeting:** What each product addresses specifically
- **Sensitivity-Safe Formulation:** Why safe for their triggers
- **Application Protocol:** How and when to use each product
- **Expected Timeline:** "See improvement in 2-4 weeks"
- **Product Cards Include:** Image, skin benefit, key ingredients, hypoallergenic badges, "Add to Routine"
- **Match Reasoning:** "Your symptoms suggest [condition], so this [ingredient] helps because..."
- **Dermatologist Endorsement:** Expert validation of recommended routine
- **Patch Test Guidance:** How to safely introduce new products
- **What to Avoid:** Trigger ingredients specific to their sensitivity
- **Routine Bundle:** Complete set with savings
- **Alternative Options:** Budget or simplified routine versions
- **CTAs:** "Start Your Routine" and "Get Complete Set"
- **Retake Option:** "Skin changed? Retake diagnostic"

**Conversion Tactics Use:**
- Medical-style diagnostic language
- Trigger identification and avoidance
- Dermatologist-backed recommendations
- Hypoallergenic/sensitivity-tested emphasis
- Patch test instructions (builds trust)
- Timeline expectations for improvement

**Key Quiz Questions to Include:**
- **Skin Feel:** Oily, dry, tight, combination zones
- **Pore Visibility:** Large, small, clogged, normal
- **Reaction History:** Redness, burning, breakouts, rashes from products
- **Sensitivity Indicators:** Stinging, itching, flaking, inflammation
- **Environmental Reactions:** Sun, wind, heat, cold sensitivity
- **Breakout Pattern:** Frequency, location, type (cystic, whiteheads, etc.)
- **Texture Issues:** Rough, smooth, bumpy, flaky
- **Ingredient Reactions:** Known allergies or sensitivities
- **Current Routine:** What they're using, frequency
- **Lifestyle Factors:** Diet, sleep, stress, climate

**Goal:** Convert skin-frustrated shoppers into confident buyers by providing clinical-level diagnosis that identifies their exact skin type and sensitivity triggers, then recommending products specifically formulated for their needs with dermatologist validation that makes switching feel medically sound and safe.`,
  },
  {
    icon: <Droplet />,
    title: "Subscription-Optimized PDP",
    description:
      "Create a product page optimized for subscriptions, emphasizing convenience and savings with testimonials about never running out.",
    prompt: `**Build a subscription-optimized product page that prioritizes recurring purchases through convenience messaging, savings emphasis, and never-run-out testimonials.**

**PRODUCT TYPE:** e.g., "Skincare essentials" OR "Supplements" OR "Personal care" (consumable products with predictable usage)

**SUBSCRIPTION MODEL:** e.g., Auto-delivery every 30/60/90 days with discount

**Visitor Type:** Choose one:
- [ ] Regular users (already buy repeatedly)
- [ ] Convenience seekers (hate remembering to reorder)
- [ ] Value shoppers (motivated by subscription savings)

**Tone & Style:**
- Convenience-first framing
- Set-it-and-forget-it ease
- Savings celebration
- "Never run out" reassurance

**CTA Guidelines:** Each CTA should:
- Prioritize subscription ("Subscribe & Save 25%")
- Use convenience language ("Never Run Out")
- Reference flexibility ("Pause Anytime")
- Emphasize savings over one-time

**Conversion Tactics Use:**
- Subscription as default/primary CTA
- One-time purchase as secondary option
- Annual savings calculator
- Cost-per-use breakdown
- Testimonials about convenience
- Flexibility badges (skip, pause, cancel)

**Key Elements to Include:**
- **Hero Section:** Product image, subscription CTA (primary), one-time option (secondary/smaller)
- **Subscription Pricing Display:**
  - Subscribe: $XX (save 25%) - LARGE, prominent
  - One-time: ~~$XX~~ - smaller, less prominent
  - "You save $X per order"
- **Subscription Benefits Box:**
  - Save 25% on every order
  - Free shipping always
  - Never run out
  - Pause or cancel anytime
  - Adjust frequency easily
- **Usage Calculator:** "This lasts X days, we'll deliver every Y days"
- **Savings Breakdown:**
  - Per order savings: "$X saved"
  - Annual savings: "Save $XXX/year"
  - Visual savings meter
- **Delivery Frequency Selector:** Every 30/45/60/90 days with recommendation
- **How Subscription Works:** 3 simple steps (Subscribe → Auto-delivered → Modify anytime)
- **Flexibility Features:**
  - Skip shipment badge
  - Pause subscription badge
  - Cancel anytime badge
  - Change frequency badge
  - All with "Easy to manage" messaging
- **Subscriber Testimonials (5-8):** Focus on convenience and never running out
- **Subscriber Perks:**
  - 25% off subscription items
  - Early access to new products
  - Exclusive subscriber-only items
  - Priority customer support
- **Product Details:** Standard info (ingredients, benefits, usage)
- **First Order Incentive:** "Get 30% off your first subscription delivery"
- **Subscription FAQs:** 5-7 questions (Cancel how? Skip? Change products? Shipping? Charge timing?)
- **Social Proof:** "120,000+ active subscribers" with faces/names
- **Never Run Out Stories:** Testimonials about forgetting to reorder vs. subscription ease
- **Comparison Visual:** Running out/emergency ordering vs. automatic delivery timeline
- **CTAs:** Primary subscribe CTA (hero + sticky), secondary one-time (less prominent)

**Goal:** Maximize subscription conversions over one-time purchases by making recurring delivery the obvious default choice through prominent savings display, convenience emphasis, flexibility reassurance, and testimonials that validate the set-it-and-forget-it value proposition while de-emphasizing one-time purchase as the less smart option.`,
  },
  {
    icon: <RotateCcw />,
    title: "Competitor Comparison Pages",
    description:
      "Create a comparison page with side-by-side ingredient lists, efficacy claims, price analysis, and money-back guarantee.",
    prompt: `**Build a competitor comparison page that positions your product as superior through transparent side-by-side analysis of ingredients, efficacy, and value.**

**COMPARISON FOCUS:** e.g., "Us vs. [Competitor]" OR "Why We're Different" OR "The Better Alternative to [Brand]"

**COMPARISON ANGLE:** e.g., Ingredient quality, clinical backing, price-per-use, formulation transparency

**Visitor Type:** Choose one:
- [ ] Active comparers (researching between brands)
- [ ] Competitor customers (considering switch)
- [ ] Value-conscious (want best quality for price)

**Tone & Style:**
- Factual and data-driven
- Confident not aggressive
- Transparent about differences
- "Here's why we're better" directness

**CTA Guidelines:** Each CTA should:
- Reference switching ("Try the Better Option")
- Use proof language ("See the Difference")
- Include guarantee ("Risk-Free Trial")
- Emphasize value ("Better Quality, Better Price")

**Conversion Tactics Use:**
- Side-by-side ingredient comparison
- Clinical study references
- Price-per-application calculations
- Efficacy claim validation
- Switcher testimonials
- Money-back guarantee emphasis

**Key Elements to Include:**
- **Hero Section:** Bold comparison headline, "Try the Better Alternative" CTA
- **Why People Switch:** Top 4-5 reasons customers leave competitors for you
- **Side-by-Side Comparison Chart (10-15 rows):**
  - Full ingredient list
  - Active ingredient concentrations
  - Clinical studies backing efficacy
  - Third-party testing
  - Allergen/irritant-free claims
  - Certifications (cruelty-free, vegan, etc.)
  - Packaging sustainability
  - Price per application/use
  - Size/volume
  - Money-back guarantee
- **Ingredient Breakdown:** Superior ingredients/forms you use vs. inferior ones they use
- **Efficacy Claims Validation:** Your clinical backing vs. their unsubstantiated claims
- **Concentration Analysis:** Active ingredient % showing therapeutic levels
- **Price Per Use:** True value calculation (total applications ÷ price)
- **What's NOT In Yours:** Harmful ingredients/fillers they include
- **Clinical Evidence:** Studies on your formula vs. lack of theirs
- **Third-Party Testing:** Your transparency vs. their self-testing only
- **Switcher Testimonials (6-8):** Former competitor customers explaining why they changed
- **Results Comparison:** Customer before/afters from both brands
- **Manufacturing Standards:** Your quality processes vs. theirs
- **Money-Back Guarantee:** "Try 60 days risk-free - if not better, full refund"
- **Sample Offer:** Low-commitment first purchase option
- **Honest Acknowledgment:** If competitor has any advantage, address transparently
- **CTAs:** Hero, after comparison chart, with testimonials, guarantee section (5-6 total)
- **FAQ:** 4-5 questions (Really better? Worth switching? Guarantee terms? Different results? Comparable price?)

**Goal:** Convert competitor customers into switchers by providing transparent, factual comparison that exposes ingredient quality differences, efficacy claim gaps, and value advantages while removing risk through money-back guarantee that proves confidence in superior product performance backed by switcher testimonials.`,
  },
  {
    icon: <Box />,
    title: "Review Gallery Landing Page",
    description:
      "Create a review page organized by concern with verified badges and photo reviews showing products in use.",
    prompt: `**Build a review gallery landing page organized by concern with verification badges and authentic photo reviews showing real product use.**

**GALLERY FOCUS:** e.g., "Real Results, Real Reviews" OR "Customer Reviews & Results" OR "See What Works"

**CONCERN CATEGORIES:** e.g., Acne, aging, dryness, sensitivity, hyperpigmentation, texture

**Gallery Visitor Type:** Choose one:
- [ ] Results-skeptics (need proof before buying)
- [ ] Concern-specific (solving particular issue)
- [ ] Photo-convinced (want visual evidence)

**Tone & Style:**
- Authentic and unfiltered
- Celebratory of real results
- Empathetic to skin struggles
- Transparent not cherry-picked

**CTA Guidelines:** Each CTA should:
- Use transformation language ("Get Their Results")
- Reference specific concern ("Solve Acne")
- Include proof element ("Verified Reviews")
- Link to concern-specific products

**Conversion Tactics Use:**
- Filter by exact concern match
- Verified purchase badges
- Photo review priority display
- Timeline tags (2 weeks, 4 weeks, etc.)
- Star rating distribution
- Review submission incentive

**Key Elements to Include:**
- **Hero Section:** Headline, review preview grid, filter options, "Submit Your Review" CTA
- **Review Stats Overview:**
  - Overall rating: 4.8/5 from 15,000+ reviews
  - Rating breakdown: 5★ 85%, 4★ 12%, etc.
  - Verification rate: "94% verified purchases"
  - Photo reviews: "8,500+ with photos"
- **Filter Options:** By skin concern, by product, by rating (4-5★ only), verified only, with photos only, by timeline
- **Review Grid:** Cards showing customer photo, name, rating, concern, timeline
- **Featured Photo Reviews (6-10 detailed):**
  - Customer name + verification badge
  - Skin concern addressed
  - Product(s) used
  - Star rating + date
  - Timeline (weeks/months)
  - Before/after photos or in-use photos
  - Full written review (100-200 words)
  - Helpful votes count
  - "Shop This Product" CTA
- **Concern-Specific Galleries:** Dedicated sections per skin issue
- **Timeline Filters:** Results at 2 weeks, 4 weeks, 8 weeks, 3+ months
- **Verification Badges:** Confirmed purchase indicators clearly displayed
- **Most Helpful Reviews:** Sorted by community votes
- **Photo vs. Text Reviews:** Prioritize photo reviews in display
- **Skin Type Labels:** Oily, dry, combo, sensitive tags for relatability
- **Product Performance Metrics:** "96% report visible improvement"
- **Video Reviews:** 3-5 customer video testimonials if available
- **Review Submission Section:** "Share your results & get 25% off next order"
- **Authenticity Promise:** "All reviews verified, unfiltered, real customers"
- **CTAs:** Top of page, with featured reviews, concern galleries (5-6 total)
- **FAQ:** 3-4 questions (How to submit? Photo requirements? Verification process? Incentive?)

**Goal:** Convert skeptical shoppers into confident buyers by showcasing authentic, concern-specific reviews with visual proof that demonstrates real results from verified customers, organized by filterable concerns and timelines that let shoppers find relatable success stories matching their exact skin issues.`,
  },
];
